import { useState } from "react";
import { useSetAtom } from "jotai";

import { type Message } from "shared/apiClient";
import { AnalysisReportEvents, MessageType } from "shared/types";
import { getText } from "shared/lib";
import { showReportVar } from "@/variables/globalVar";

import { useChannel, createMessageEvent } from "shared/channel";
import { useAccount } from "@/hooks";
import {
  RoutingRenderType,
  useRoutingRenderType,
  type RoutingQuestionData,
} from "./hooks";

import { TemplateRenderer } from "shared/components";
import { AnalysisDisclaimerPanel } from "@/components/analysisDiscliamer";
import { OffTopicPanelContainer } from "@/components/wizard/offTopicPanel";
import { DisclaimerCallout } from "@/components/analysisDiscliamer/DisclaimerCallout";
import { QuestionLoader } from "@/components/wizard/questionLoader";

export interface RoutingStepProps {
  disabled?: boolean;
  message: Message | undefined | null;
  showLoading?: boolean;
}

export function RoutingStep({
  disabled,
  message,
  showLoading,
}: RoutingStepProps) {
  const questionData: RoutingQuestionData = message?.question_data || {};

  const setShowReportVar = useSetAtom(showReportVar);

  const { channel } = useChannel();

  const { conversation } = useAccount();

  const [submitting, setSubmitting] = useState(false);

  const routingRenderType = useRoutingRenderType({ conversation, message });

  const renderDisclaimers = () => {
    if (
      Array.isArray(questionData?.analysis_callouts) &&
      questionData.analysis_callouts.length > 0
    ) {
      return questionData?.analysis_callouts.map((callout) => {
        return (
          <DisclaimerCallout
            className="[&_a]:text-textLink [&_a]:hover:text-textLinkHover flex flex-col gap-1 [&_a]:underline"
            title={callout.title}
            type={callout.color}
          >
            <div>
              <TemplateRenderer
                template={callout.content}
                renderFunctions={{
                  restart_conversation: () => {
                    return (
                      <a
                        className="text-textLink hover:text-textLinkHover cursor-pointer underline"
                        onClick={() => {
                          const customEvent = new CustomEvent(
                            AnalysisReportEvents.RESTART_CONVERSATION,
                          );
                          document.dispatchEvent(customEvent);
                        }}
                      >
                        {getText("restart")}
                      </a>
                    );
                  },
                }}
              />
            </div>
          </DisclaimerCallout>
        );
      });
    }

    return [];
  };

  if (showLoading) {
    return <QuestionLoader />;
  }

  switch (routingRenderType) {
    case RoutingRenderType.OFF_TOPIC:
      return <OffTopicPanelContainer message={message} />;
    case RoutingRenderType.DISCLAIMER:
      return (
        <AnalysisDisclaimerPanel
          conversation={conversation}
          disclaimers={renderDisclaimers()}
          onClickSubmit={() => {
            // TODO: report the show report setter once we transition over to
            //  conversation routing flow
            setShowReportVar({
              conversationId: conversation?.id || "",
              visible: true,
            });

            if (channel && conversation?.id) {
              setSubmitting(true);

              const messageDescriptor = createMessageEvent({
                conversation_id: conversation.id,
                message_type: MessageType.DISCLAIMER_ACCEPTANCE,
                text: getText("accepted"),
              });

              console.log(
                "<RoutingStep> handleClickGenerateReport()",
                messageDescriptor,
              );
              channel.sendMessage(messageDescriptor);
            }
          }}
          submitting={submitting}
        />
      );
  }
}
